import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { PhotoMark } from "./PhotoMark";

const POSTERS = [
  { src: "/posters/cartel-5.webp", caption: "«Con un solo corazón (bis)» — cartel oficial del espectáculo" },
  { src: "/posters/cartel-1.webp", caption: "«Esencia de Bolero» — Teatro Victoria, Talavera de la Reina" },
  { src: "/posters/cartel-2.webp", caption: "Gira 2017 — Homenaje a los grandes tríos del bolero y del son" },
  { src: "/posters/cartel-4.webp", caption: "Homenaje a los grandes tríos del bolero — Oropesa, Toledo" },
  { src: "/posters/cartel-3.jpg", caption: "Gira 20 aniversario · 1998-2018 — «Que veinte años no es nada»" },
  { src: "/posters/cartel-6.webp", caption: "«Con un solo corazón» — Presentación Tour 2025 · Teatro Victoria, Talavera de la Reina" },
  { src: "/posters/cartel-7.png", caption: "Tour 2025 — «Con un solo corazón»" },
  { src: "/posters/cartel-8.webp", caption: "Sinfonía de Boleros por Etiopía — Concierto solidario con la Orquesta Eusebio Rubalcaba" },
  { src: "/posters/cartel-9.webp", caption: "¡Viva México! — Corridos, rancheras y boleros en estado puro" },
  { src: "/posters/cartel-10.jpg", caption: "«Inmortal» — Homenaje a María Dolores Pradera — Oropesa, Toledo" },
  { src: "/posters/cartel-11.png", caption: "«Música de siempre para siempre» — Única actuación en Talavera · Restaurante Doña Inés" },
  { src: "/posters/cartel-12.webp", caption: "«Machín en Trío» — Tributo al inolvidable Antonio Machín · feat. Cuarto Bolero" },
];

export const Carteleria = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (active) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [active]);

  return (
    <section id="carteleria" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <SectionHeader
          eyebrow="Cartelería"
          title={
            <>
              La imagen del <span className="italic text-gold">directo</span>.
            </>
          }
        />

        <div className="mt-14 flex flex-wrap justify-center gap-8">
          {POSTERS.map((p, i) => (
            <Reveal key={p.src} delay={i * 0.06} className="w-full sm:w-[calc(33.333%-1.4rem)]">
              <button
                data-testid={`cartel-item-${i + 1}`}
                onClick={() => setActive(p)}
                className="group relative block w-full overflow-hidden"
              >
                <img src={p.src} alt={p.caption} loading="lazy" className="w-full aspect-[2/3] object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <p className="absolute bottom-4 left-4 right-4 font-serif italic text-lg text-cream opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 text-left">
                  {p.caption}
                </p>
                <PhotoMark className="top-4 right-4 h-10 w-auto opacity-90" />
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 text-sm text-mute text-justify">
            Material oficial de difusión. Para carteles de eventos concretos, escríbenos y preparamos la cartelería de tu concierto.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="cartel-lightbox"
            className="fixed inset-0 z-[100] bg-ink/[0.96] backdrop-blur-sm flex flex-col items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
          >
            <button className="absolute top-6 right-6 text-cream/70 hover:text-gold transition-colors p-2" data-testid="cartel-close-button" aria-label="Cerrar">
              <X size={30} strokeWidth={1.5} />
            </button>
            <motion.img
              src={active.src}
              alt={active.caption}
              className="max-h-[80vh] max-w-[92vw] object-contain"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-5 font-serif italic text-lg text-sand">{active.caption}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
